import { useEffect, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { overlayHoldsArrows } from './stepThrough';
import { useLanguage } from '../../contexts/LanguageContext';

// WHICH STEP'S QUESTION IS ON SCREEN, and the two arrows that change it.
//
// The search makes one call per step, and the panel showed the first one's on the argument
// that thirty copies of one question are not thirty questions. That is true of the WORDS and
// false of everything else in it: the parts named change with every step, and so does the
// drawing above them — which is the half anybody opens this to check.
//
// THE KEYS ARE BOUND IN THE CAPTURE PHASE, and that is not a detail. The part selector behind
// this modal listens for the same two keys on `window` and calls stopImmediatePropagation, and
// it bound first, so a listener added when this panel opened would never run. An open panel is
// what the arrows are for while it is open. See useArrowSteps for the other half of the
// argument, which is why that stepper is so jealous of them in the first place.
//
// AND ONE PER PHOTOGRAPH, for the listing crops: `labelOf` names the entries instead of steps,
// and `vertical` takes ↑ ↓ instead of ← →.
//
// THE ARROWS STAY WHERE THEY ARE while it is walked: the middle is as wide as its widest entry —
// every entry's words are laid in the one cell, unseen but the one on screen — up to a cap past
// which they wrap. One navigator's width, not the app's: each is as wide as its own entries.
export function StepPromptNav({ steps, at, onGo, labelOf, subOf, vertical = false }: {
  // The step numbers, in order — not indices: the label prints one and the caller indexes by
  // position, and the two must not be the same variable.
  steps: number[];
  at: number;
  onGo: (index: number) => void;
  // What each entry is called, by position, where it is not a step.
  labelOf?: (index: number) => string;
  // A second line under an entry's label, centred — what it covers, where that is long.
  subOf?: (index: number) => string | undefined;
  vertical?: boolean;
}) {
  const { t } = useLanguage();
  // Through a ref so the effect binds once: `onGo` is a fresh closure on every render of the
  // table behind this, and rebinding on each would keep losing the capture-phase race it just
  // won.
  const go = useRef<(by: number) => void>(() => {});
  go.current = (by: number) => {
    const next = at + by;
    if (next >= 0 && next < steps.length) onGo(next);
  };

  useEffect(() => {
    if (steps.length < 2) return;
    const onKey = (e: KeyboardEvent) => {
      const [back, forth] = vertical ? ['ArrowUp', 'ArrowDown'] : ['ArrowLeft', 'ArrowRight'];
      if (e.key !== back && e.key !== forth) return;
      // An open picture over the badge steps through ITS set — see overlayHoldsArrows.
      if (overlayHoldsArrows()) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (el?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      go.current(e.key === back ? -1 : 1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [steps.length, vertical]);
  const Prev = vertical ? ChevronUp : ChevronLeft;
  const Next = vertical ? ChevronDown : ChevronRight;

  if (steps.length < 2) return null;

  // ON A WHITE PILL, in either theme: the window around it is always the page's blue, and slate
  // on that blue was the washed-out row this replaced. Light colours only, for that reason.
  const arrow = 'rounded-full border border-slate-300 p-1 text-slate-500 transition-colors hover:border-blue-500 hover:text-blue-600 disabled:cursor-default disabled:opacity-30';
  return (
    // Its own row above the panel's heading, OUTSIDE the part that scrolls (see CallModal's
    // header): the panel scrolls for hundreds of lines and a stepper that scrolled away with
    // the first paragraph would be missing exactly when somebody wants the next step.
    <div className="flex justify-center py-1">
    <div className="inline-flex items-center gap-3 rounded-full bg-white px-1.5 py-1 shadow-sm">
      <button
        type="button"
        onClick={() => go.current(-1)}
        disabled={at <= 0}
        title={t('notice.pdf.prev')}
        aria-label={t('notice.pdf.prev')}
        className={arrow}
      >
        <Prev className="h-3.5 w-3.5" />
      </button>
      <span className="grid max-w-md text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
        {steps.map((step, i) => {
          const sub = subOf?.(i);
          return (
            <span
              key={i}
              aria-hidden={i !== at}
              className={`col-start-1 row-start-1 flex flex-col items-center justify-center ${i === at ? '' : 'invisible'}`}
            >
              {/* THE LABEL — over several lines where it says so — and WHERE IN THE WALK, on a row of its own. */}
              <span className="whitespace-pre-line">{labelOf?.(i) ?? `${t('notice.parts.stepRow')} ${step}`}</span>
              <span className="tabular-nums">{`${i + 1} / ${steps.length}`}</span>
              {sub && <span className="text-[11px] font-normal normal-case tracking-normal text-slate-500">{sub}</span>}
            </span>
          );
        })}
      </span>
      <button
        type="button"
        onClick={() => go.current(1)}
        disabled={at >= steps.length - 1}
        title={t('notice.pdf.next')}
        aria-label={t('notice.pdf.next')}
        className={arrow}
      >
        <Next className="h-3.5 w-3.5" />
      </button>
    </div>
    </div>
  );
}
